// "Prihranek pri prepisu": shortening long silences before transcription (one switch for the whole
// firm – providers bill by audio duration) and how much audio was actually sent to the provider.
import { useState } from 'react';
import type { AppSettings, UsageStats, UsageTotals } from '../../../../shared/types';
import { getServerSettings, getUsage, saveTrimSilence } from '../../lib/api';
import { formatHours, plural } from '../../lib/format';
import { accountErrorText } from '../../components/AccountForms';
import { Icon } from '../../components/Icon';
import { toast } from '../../components/Toasts';
import { LoadError, Loading, useLoaded } from './common';

interface SavingsData {
  settings: AppSettings;
  usage: UsageStats;
}

async function loadSavings(): Promise<SavingsData> {
  const [settings, usage] = await Promise.all([getServerSettings(), getUsage()]);
  return { settings, usage };
}

/** Share of the recorded audio that was not sent, in whole percent (0–100). */
export function savedPercent(t: Pick<UsageTotals, 'recordedMs' | 'sentMs'>): number {
  if (!(t.recordedMs > 0)) return 0;
  return Math.min(100, Math.max(0, Math.round((1 - t.sentMs / t.recordedMs) * 100)));
}

/** "posneto 12:34:00, poslano v prepis 9:58:00 – prihranek 21 %" */
export function usageLine(t: UsageTotals): string {
  return `posneto ${formatHours(t.recordedMs)}, poslano v prepis ${formatHours(t.sentMs)} – prihranek ${savedPercent(t)} %`;
}

function UsageText({ usage }: { usage: UsageStats }) {
  const { last30Days, allTime } = usage;
  return (
    <div className="savings-usage">
      <p className="savings-usage__main" data-testid="usage-30">
        {last30Days.clips > 0 ? (
          <>V zadnjih 30 dneh: {usageLine(last30Days)}</>
        ) : (
          'V zadnjih 30 dneh ni bilo prepisanih posnetkov.'
        )}
      </p>
      {allTime.clips > last30Days.clips && (
        <p className="muted small" data-testid="usage-all">
          Skupaj: {usageLine(allTime)} ({plural(allTime.clips, ['posnetek', 'posnetka', 'posnetki', 'posnetkov'])})
        </p>
      )}
      <p className="muted small">
        Upoštevani so posnetki, prepisani v tem obdobju (pri ponovnem prepisu zadnji prepis), za vse uporabnike skupaj.
      </p>
    </div>
  );
}

export function SavingsSection() {
  const loaded = useLoaded(loadSavings, 'Podatkov o prihranku ni bilo mogoče naložiti.');
  const [busy, setBusy] = useState(false);
  const data = loaded.data;

  const toggle = async () => {
    if (!data) return;
    const next = !data.settings.trimSilence;
    setBusy(true);
    try {
      const settings = await saveTrimSilence(next);
      loaded.set({ ...data, settings });
      toast(
        settings.trimSilence
          ? 'Daljše tišine bodo pred prepisom izpuščene.'
          : 'Posnetki bodo poslani v prepis v celoti, s tišinami vred.',
        { kind: 'success' },
      );
    } catch (err) {
      toast(accountErrorText(err, 'Nastavitve ni bilo mogoče shraniti.'), { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" aria-labelledby="savings-title" id="prihranek">
      <h2 id="savings-title" className="card__title">
        Prihranek pri prepisu
      </h2>
      {loaded.error && !data ? (
        <LoadError message={loaded.error} onRetry={loaded.reload} />
      ) : !data ? (
        <Loading />
      ) : (
        <>
          <label className="switch-row" htmlFor="trim-silence">
            <span className="switch-row__text">
              <span className="switch-row__title">Izpusti daljše tišine pred prepisom (cenejši prepis)</span>
              <span className="muted small">
                Ponudnik prepisa zaračuna vsako minuto posnetka, tudi tišino. Premori, daljši od dveh sekund, se pred pošiljanjem
                skrajšajo na pol sekunde; krajši premori in tih govor ostanejo nedotaknjeni. Poslušate še vedno celoten posnetek in
                časovne oznake v prepisu kažejo nanj. Velja za vse uporabnike.
              </span>
            </span>
            <input
              id="trim-silence"
              type="checkbox"
              role="switch"
              className="switch"
              checked={data.settings.trimSilence}
              disabled={busy}
              onChange={() => void toggle()}
            />
          </label>
          {!data.settings.trimSilence && (
            <p className="muted small" role="status">
              <Icon name="info" size={16} /> Izklopljeno: posnetki se v prepis pošiljajo v celoti.
            </p>
          )}
          <UsageText usage={data.usage} />
        </>
      )}
    </section>
  );
}
